// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
//</copyright>
// ----------------------------------------------------------------------
//

import { PERMISSION_KEY, PERMISSION_OPERATOR } from 'logic/data/Izenda.AuthorizationConstants';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';
import { filter, isNil } from 'lodash/fp';



/**
 * Filter delivery methods based on current permissions
 * @param {{value: string}[]} deliveryMethods Delivery methods
 * @param {'Email' | 'File Location'} deliveryType Delivery type
 * @param {{value: string}[]} attachmentTypes Attachment types
 * @returns {{value: string}[]} Allowed delivery methods
 */
export function filterAllowedSubscriptionDeliveryMethods(deliveryMethods, deliveryType,
                                                         attachmentTypes) {
  if (isNil(deliveryMethods))
    return [];
  if (deliveryType !== 'Email')
    return deliveryMethods;
  // only email delivery methods can be filtered now
  const authorization = new SecurityManager();
  const hasLinkPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingDeliveryMethodLink]);
  let hasAttachmentPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingDeliveryMethodAttachment]);
  hasAttachmentPrivilege = hasAttachmentPrivilege && filterAllowedSubscriptionAttachmentTypes(attachmentTypes).length > 0;
  const hasEmbeddedPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingDeliveryMethodEmbeddedHTML]);
  return filter(
    fileType =>
      (hasLinkPrivilege || fileType.value !== 'Link') &&
      (hasAttachmentPrivilege || fileType.value !== 'Attachment') &&
      (hasEmbeddedPrivilege || fileType.value !== 'Embedded HTML'),
    deliveryMethods
  );
}

/**
 * Filter attachment types based on current permissions
 * @param {{value: string}[]} attachmentTypes Attachment types
 * @returns {{value: string}[]} Allowed attachment types
 */
export function filterAllowedSubscriptionAttachmentTypes(attachmentTypes) {
  if (isNil(attachmentTypes))
    return [];
  const authorization = new SecurityManager();
  const hasPdfPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingAttachmentTypePdf]);
  const hasWordPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingAttachmentTypeWord]);
  const hasExcelPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingAttachmentTypeExcel]);
  const hasCsvPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingAttachmentTypeCsv]);
  const hasXmlPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingAttachmentTypeXml]);
  const hasJsonPrivilege = authorization.checkPrivileges([PERMISSION_KEY.EmailingAttachmentTypeJson]);

  return filter(
    fileType =>
      (hasWordPrivilege || fileType.value !== 'Word') &&
      (hasExcelPrivilege || fileType.value !== 'Excel') &&
      (hasPdfPrivilege || fileType.value !== 'PDF') &&
      (hasCsvPrivilege || fileType.value !== 'CSV') &&
      (hasXmlPrivilege || fileType.value !== 'XML') &&
      (hasJsonPrivilege || fileType.value !== 'JSON'),
    attachmentTypes
  );
}

/**
 * Filter file types based on current permissions
 * @param {{value: string}[]} fileTypes File types
 * @returns {{value: string}[]} Allowed file types
 */
export function filterAllowedSubscriptionExportFileTypes(fileTypes) {
  if (isNil(fileTypes))
    return [];
  const authorization = new SecurityManager();
  const hasPdfPrivilege = authorization.checkPrivileges([PERMISSION_KEY.ExportingExportingFormatPdf]);
  const hasWordPrivilege = authorization.checkPrivileges([PERMISSION_KEY.ExportingExportingFormatWord]);
  const hasExcelPrivilege = authorization.checkPrivileges([PERMISSION_KEY.ExportingExportingFormatExcel]);
  const hasCsvPrivilege = authorization.checkPrivileges([PERMISSION_KEY.ExportingExportingFormatCsv]);
  const hasXmlPrivilege = authorization.checkPrivileges([PERMISSION_KEY.ExportingExportingFormatXml]);
  const hasJsonPrivilege = authorization.checkPrivileges([PERMISSION_KEY.ExportingExportingFormatJson]);

  return filter(
    fileType =>
      (hasWordPrivilege || fileType.value !== 'Word') &&
      (hasExcelPrivilege || fileType.value !== 'Excel') &&
      (hasPdfPrivilege || fileType.value !== 'PDF') &&
      (hasCsvPrivilege || fileType.value !== 'CSV') &&
      (hasXmlPrivilege || fileType.value !== 'XML') &&
      (hasJsonPrivilege || fileType.value !== 'JSON'),
    fileTypes
  );
}

/**
 * Checks whether any kind of below subscription is permitted:
 * - email delivery methods
 * - export types for "send to file"
 * @returns {boolean} true - if we have at least one subscription permission
 */
export function isAnySubscriptionAvailable() {
  const authorization = new SecurityManager();
  return authorization.checkPrivileges([
    PERMISSION_KEY.EmailingDeliveryMethodLink,
    PERMISSION_KEY.EmailingDeliveryMethodAttachment,
    PERMISSION_KEY.EmailingDeliveryMethodEmbeddedHTML,
    PERMISSION_KEY.ExportingExportingFormatPdf,
    PERMISSION_KEY.ExportingExportingFormatWord,
    PERMISSION_KEY.ExportingExportingFormatExcel,
    PERMISSION_KEY.ExportingExportingFormatCsv,
    PERMISSION_KEY.ExportingExportingFormatXml,
    PERMISSION_KEY.ExportingExportingFormatJson
  ], PERMISSION_OPERATOR.Or);
}



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.PermissionUtils.js